import {
    createContext, useContext, useMemo,
    useCallback,
} from 'react';

import { getStorage } from 'hooks/useStorage';
import { USER_TOKEN_KEY } from 'hooks/keys';
import { useDispatch, useSelector } from 'react-redux';
import { setToken } from 'store/reducers/authSlice';
import { getToken } from 'store/selectors/authSelectors';

type AuthContextProps = {
    login: (token: string) => void;
    logout: () => void;
    isAuthenticated: () => boolean;
}

type Props = {
    children: JSX.Element;
}

const AuthContext = createContext<AuthContextProps>({
    login: (token: string) => {},
    logout: () => {},
    isAuthenticated: () => false,
});

const { setItem, removeItem, getItem } = getStorage('local');

export const AuthProvider = ({ children }: Props) => {
    const dispatch = useDispatch();
    const currentToken = useSelector(getToken);
    
    const login = useCallback((token: string) => {
        setItem(USER_TOKEN_KEY, token);
        dispatch(setToken(token));
    }, [dispatch]);

    const logout = useCallback(() => {
        removeItem(USER_TOKEN_KEY);
    }, []);

    const isAuthenticated = useCallback(() => Boolean(currentToken?.length), [currentToken]);

    const providerValue = useMemo(() => ({
        login, logout, isAuthenticated, currentToken,
    }), [currentToken, isAuthenticated, login, logout]);

    return (
        <AuthContext.Provider value={providerValue}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
